/**
 * markdownToSpeakableText
 * -----------------------
 * Convert a markdown string into clean prose suitable for text-to-speech.
 * Strips markdown syntax so a speech engine doesn't read "#", "**", link URLs,
 * table pipes, etc. aloud. Fenced and inline code are removed entirely — lesson
 * audio reads the explanation, not the code.
 *
 * @param {string} md
 * @returns {string} plain, speakable prose
 */
export const markdownToSpeakableText = (md) => {
    if (!md || typeof md !== "string") return "";

    let text = md.replace(/\r\n?/g, "\n");

    // Fenced code blocks (```...``` and ~~~...~~~) — don't read code aloud.
    text = text.replace(/```[\s\S]*?```/g, " ");
    text = text.replace(/~~~[\s\S]*?~~~/g, " ");

    // Images ![alt](url) — drop entirely.
    text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");

    // Inline links [text](url) -> text ; reference links [text][ref] -> text.
    text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
    text = text.replace(/\[([^\]]+)\]\[[^\]]*\]/g, "$1");
    // Reference definitions:  [ref]: https://…  -> drop the whole line.
    text = text.replace(/^\s*\[[^\]]+\]:\s*\S+.*$/gm, " ");

    // Inline code `code` -> keep the words, drop the backticks.
    text = text.replace(/`([^`]+)`/g, "$1");

    // Heading markers, blockquotes, horizontal rules.
    text = text.replace(/^\s{0,3}#{1,6}\s*/gm, "");
    text = text.replace(/^\s{0,3}>+\s?/gm, "");
    text = text.replace(/^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/gm, " ");

    // List markers (unordered + ordered) at line start.
    text = text.replace(/^\s*[-*+]\s+/gm, "");
    text = text.replace(/^\s*\d+\.\s+/gm, "");

    // Tables: drop separator rows (|---|---|) then turn pipes into spaces.
    text = text.replace(/^\s*\|?[ :]*-{2,}[-| :]*\|?\s*$/gm, " ");
    text = text.replace(/\|/g, " ");

    // Emphasis / bold / strikethrough markers.
    text = text.replace(/(\*\*|__)(.*?)\1/g, "$2");
    text = text.replace(/(\*|_)(.*?)\1/g, "$2");
    text = text.replace(/~~(.*?)~~/g, "$1");

    // Stray HTML tags + common entities.
    text = text.replace(/<[^>]+>/g, " ");
    text = text
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

    // Normalize whitespace: collapse spaces, cap blank lines, trim each line.
    text = text.replace(/[ \t]+/g, " ");
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text
        .split("\n")
        .map((line) => line.trim())
        .join("\n")
        .trim();

    return text;
};

export default markdownToSpeakableText;
